import React, { useState, ComponentType } from 'react';
import { ForgePopover, ForgeIconButton, ForgeIcon } from '@tylertech/forge-react';
import { IPopoverToggleEventData } from '@tylertech/forge';

import { I18n } from 'common/i18n';
import { revisionSeqFromCatalogId } from 'common/cetera/catalog_id';

import { ActionItemList } from './ActionItemList';
import { useActionModal } from './hooks/useActionModal';
import { useActionDropdownData } from './hooks/useActionDropdownData';

export interface ActionDropdownProps {
  uid: string;
  link: string;
  updateAudience?: (payload: any) => void;
  transferOwnership?: (payload: any) => void;
  type?: string;
  isCatalogFederatedView?: boolean;
  isDataFederatedView?: boolean;
  sourceLink?: string;
}

export const ActionDropdown = ({
  uid,
  link,
  updateAudience,
  transferOwnership,
  type,
  isCatalogFederatedView,
  isDataFederatedView,
  sourceLink,
}: ActionDropdownProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { currentModal, openModal, closeModal } = useActionModal();
  const {
    fetchedApprovalsGuidance,
    failedViewFetch,
    fetchingViewAndGuidance,
    view,
    fetchDataIfNeeded,
    setNeedsViewUpdate,
  } = useActionDropdownData({ uid, isCatalogFederatedView, isDataFederatedView });

  const getTranslation = (key: string) => {
    return I18n.t(`shared.asset_browser.result_list_table.action_dropdown.${key}`);
  };

  const setCurrentModal = (Modal: ComponentType<any>, modalProps: any = {}) => {
    openModal(Modal, modalProps, view);
    setIsPopoverOpen(false);
  };

  const closeCurrentModal = () => {
    closeModal();
  };

  const renderContent = () => {
    if (fetchingViewAndGuidance) {
      // render a spinner
      return (
        <div className="action-dropdown-spinner-container">
          <span className="spinner-default"></span>
        </div>
      );
    }

    // show an error if we failed to fetch the view
    if (failedViewFetch) {
      return <div className="error-message">{getTranslation('permissions_error')}</div>;
    }

    const isRevision = !!(uid && uid.match(/^\w{4}-\w{4}:\d+$/));
    const isStoryDraft = !!(uid && uid.match(/^\w{4}-\w{4}:draft$/));
    const revision = isRevision ? { revisionSeq: revisionSeqFromCatalogId(uid), link } : null;

    // render the list of possible actions
    return (
      <ActionItemList
        approvalsGuidance={fetchedApprovalsGuidance}
        view={view}
        revisionInfo={revision}
        setCurrentModal={setCurrentModal}
        closeCurrentModal={closeCurrentModal}
        assetIsStoryDraft={isStoryDraft}
        assetType={type}
        updateView={setNeedsViewUpdate}
        updateAudience={updateAudience}
        transferOwnership={transferOwnership}
        isCatalogFederatedView={isCatalogFederatedView}
        isDataFederatedView={isDataFederatedView}
        sourceLink={sourceLink}
        link={link}
      />
    );
  };

  // Don't render for system datasets
  if (type === 'system_dataset') {
    return null;
  }

  const handlePopoverToggle = (event: CustomEvent<IPopoverToggleEventData>) => {
    const newState = event.detail.newState === 'open';
    setIsPopoverOpen(newState);

    // Fetch view and guidance when opening
    if (newState) {
      fetchDataIfNeeded();
    }
  };

  return (
    <>
      <ForgeIconButton
        aria-label={getTranslation('title')}
        className="action-dropdown-button"
        data-testid="action-dropdown-button"
        onClick={() => setIsPopoverOpen((prev) => !prev)}
      >
        <ForgeIcon name="more_vert" />
      </ForgeIconButton>

      <ForgePopover placement="bottom-end" open={isPopoverOpen} on-forge-popover-toggle={handlePopoverToggle}>
        <div className="forge-action-dropdown-content">{renderContent()}</div>
      </ForgePopover>

      {currentModal}
    </>
  );
};
